import { useEffect, useMemo, useState } from "react";
import { MediaField } from "@/components/admin/MediaField";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listAdminTestimonials,
  createTestimonial,
  updateTestimonial,
  deleteTestimonial,
  reorderTestimonials,
  upsertSchema,
  isValidHttpsUrl,
  type AdminTestimonial,
  type TestimonialInput,
} from "@/lib/admin/testimonials.functions";

const INVALID_IMAGE_URL_MSG =
  "This URL is not a direct image URL. Use a URL such as https://images.unsplash.com/... or upload the image to storage.";

export const Route = createFileRoute("/_admin/admin/testimonials")({
  component: TestimonialsAdminPage,
});

type FormState = {
  customer_name: string;
  designation: string;
  company: string;
  testimonial: string;
  rating: number;
  avatar_url: string;
  alt_text: string;
  is_active: boolean;
  is_demo: boolean;
};

function emptyForm(): FormState {
  return {
    customer_name: "",
    designation: "",
    company: "",
    testimonial: "",
    rating: 5,
    avatar_url: "",
    alt_text: "",
    is_active: false,
    is_demo: false,
  };
}

function fromRow(r: AdminTestimonial): FormState {
  return {
    customer_name: r.customer_name,
    designation: r.designation,
    company: r.company,
    testimonial: r.testimonial,
    rating: r.rating,
    avatar_url: r.avatar_url ?? "",
    alt_text: r.alt_text ?? "",
    is_active: r.is_active,
    is_demo: r.is_demo,
  };
}

const REORDER_ERROR_MSG: Record<string, string> = {
  incomplete_id_list: "Reorder must include every testimonial. Reloading the list.",
  duplicate_ids: "Reorder contained duplicate rows.",
  unknown_id: "Reorder referenced an unknown testimonial.",
  empty_id_list: "Nothing to reorder.",
  forbidden: "You don't have permission to reorder testimonials.",
};

function TestimonialsAdminPage() {
  const queryClient = useQueryClient();
  const listFn = useServerFn(listAdminTestimonials);
  const createFn = useServerFn(createTestimonial);
  const updateFn = useServerFn(updateTestimonial);
  const deleteFn = useServerFn(deleteTestimonial);
  const reorderFn = useServerFn(reorderTestimonials);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["admin", "testimonials"],
    queryFn: () => listFn(),
    staleTime: 10_000,
  });

  const [editing, setEditing] = useState<AdminTestimonial | "new" | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState<AdminTestimonial | null>(null);
  const [imageStatus, setImageStatus] = useState<"idle" | "loading" | "ok" | "error">("idle");

  useEffect(() => {
    if (editing === "new") setForm(emptyForm());
    else if (editing) setForm(fromRow(editing));
  }, [editing]);

  const trimmedImageUrl = form.avatar_url.trim();
  const previewUrl = isValidHttpsUrl(trimmedImageUrl) ? trimmedImageUrl : "";

  // The Media Library field renders its own preview, so a valid reference is
  // enough to unblock save.
  useEffect(() => {
    setImageStatus(previewUrl ? "ok" : "idle");
  }, [previewUrl]);

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["admin", "testimonials"] }),
      queryClient.invalidateQueries({ queryKey: ["testimonials", "public"] }),
    ]);

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; patch: TestimonialInput }) => {
      if (payload.id === null) {
        return createFn({ data: payload.patch as never });
      }
      return updateFn({ data: { id: payload.id, patch: payload.patch } as never });
    },
    onSuccess: async () => {
      toast.success("Testimonial saved");
      setEditing(null);
      setErrors({});
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Couldn't save the testimonial: ${msg}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } as never }),
    onSuccess: async () => {
      toast.success("Testimonial deleted");
      setConfirmDelete(null);
      await invalidate();
    },
    onError: (err) => {
      console.error(err);
      toast.error("Couldn't delete the testimonial.");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => reorderFn({ data: { ids } as never }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ["admin", "testimonials"] });
      const previous = queryClient.getQueryData<AdminTestimonial[]>([
        "admin",
        "testimonials",
      ]);
      if (previous) {
        const map = new Map(previous.map((p) => [p.id, p]));
        const reordered = ids
          .map((id, i) => {
            const row = map.get(id);
            return row ? { ...row, sort_order: (i + 1) * 10 } : null;
          })
          .filter((r): r is AdminTestimonial => r !== null);
        queryClient.setQueryData(["admin", "testimonials"], reordered);
      }
      return { previous };
    },
    onError: async (err, _vars, ctx) => {
      console.error(err);
      if (ctx?.previous) {
        queryClient.setQueryData(["admin", "testimonials"], ctx.previous);
      }
      const code = err instanceof Error ? err.message : "reorder_failed";
      toast.error(REORDER_ERROR_MSG[code] ?? "Couldn't reorder testimonials.");
      await queryClient.invalidateQueries({ queryKey: ["admin", "testimonials"] });
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const rows = useMemo(() => data ?? [], [data]);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    const ids = rows.map((p) => p.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved);
    reorderMutation.mutate(ids);
  };

  const handleSave = () => {
    const parsed = upsertSchema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      toast.error("Please fix the highlighted fields.");
      return;
    }
    if (previewUrl && imageStatus !== "ok") {
      setErrors({ avatar_url: INVALID_IMAGE_URL_MSG });
      toast.error("Please fix the highlighted fields.");
      return;
    }
    setErrors({});
    const id = editing && editing !== "new" ? editing.id : null;
    saveMutation.mutate({ id, patch: parsed.data });
  };

  if (isLoading) {
    return (
      <div>
        <AdminPageHeader title="Testimonials" description="Manage the testimonials shown on the public site." />
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <AdminPageHeader title="Testimonials" />
        <Alert variant="destructive">
          <AlertDescription>
            Couldn't load testimonials.{" "}
            <button className="underline" onClick={() => refetch()}>Retry</button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader
        title="Testimonials"
        description="Create, edit, reorder and activate the testimonials shown on the homepage."
        actions={<ImportButtons moduleId="testimonials" />}
      />

      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {rows.length} testimonial{rows.length === 1 ? "" : "s"}
        </p>
        <Button onClick={() => setEditing("new")} className="gap-2">
          <Plus className="h-4 w-4" aria-hidden /> New testimonial
        </Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No testimonials yet. Create the first one.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="p-3 w-24">Order</th>
                <th className="p-3">Customer</th>
                <th className="p-3 w-20">Rating</th>
                <th className="p-3">Flags</th>
                <th className="p-3">Status</th>
                <th className="p-3 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => (
                <tr key={p.id} className="border-t border-border">
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || reorderMutation.isPending}
                        aria-label="Move up"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => move(i, 1)}
                        disabled={i === rows.length - 1 || reorderMutation.isPending}
                        aria-label="Move down"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-foreground">{p.customer_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {[p.designation, p.company].filter((s) => s.trim().length > 0).join(" · ")}
                    </div>
                  </td>
                  <td className="p-3 text-foreground">{p.rating}/5</td>
                  <td className="p-3">
                    {p.is_demo ? <Badge variant="outline">Demo</Badge> : null}
                  </td>
                  <td className="p-3">
                    {p.is_active ? (
                      <Badge variant="secondary">Active</Badge>
                    ) : (
                      <Badge variant="outline">Hidden</Badge>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => setEditing(p)} aria-label="Edit">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setConfirmDelete(p)}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editing === "new" ? "New testimonial" : "Edit testimonial"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <FieldRow label="Customer name *" error={errors.customer_name}>
              <Input
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              />
            </FieldRow>
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldRow label="Designation" error={errors.designation}>
                <Input
                  value={form.designation}
                  onChange={(e) => setForm({ ...form, designation: e.target.value })}
                />
              </FieldRow>
              <FieldRow label="Company" error={errors.company}>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
              </FieldRow>
            </div>
            <FieldRow label="Testimonial *" error={errors.testimonial}>
              <Textarea
                rows={5}
                value={form.testimonial}
                onChange={(e) => setForm({ ...form, testimonial: e.target.value })}
              />
            </FieldRow>
            <FieldRow label="Rating (1–5) *" error={errors.rating}>
              <Input
                type="number"
                min={1}
                max={5}
                step={1}
                value={form.rating}
                onChange={(e) =>
                  setForm({ ...form, rating: Number(e.target.value) })
                }
              />
            </FieldRow>
            <FieldRow
              label="Avatar URL"
              hint="Optional. Must be a valid https:// URL. Leave blank to show the customer's initial."
              error={
                errors.avatar_url ??
                (previewUrl && imageStatus === "error" ? INVALID_IMAGE_URL_MSG : undefined)
              }
            >
              <MediaField
                value={form.avatar_url}
                onChange={(next) => setForm({ ...form, avatar_url: next })}
                onAltSuggestion={(alt) =>
                  setForm((prev) => (prev.alt_text.trim() ? prev : { ...prev, alt_text: alt }))
                }
              />
            </FieldRow>
            <FieldRow
              label="Avatar alt text"
              hint="Optional. Describe the person for screen readers."
              error={errors.alt_text}
            >
              <Input
                value={form.alt_text}
                onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
              />
            </FieldRow>
            <div className="flex items-center gap-3">
              <Switch
                id="is_demo"
                checked={form.is_demo}
                onCheckedChange={(v) => setForm({ ...form, is_demo: v })}
              />
              <Label htmlFor="is_demo">Demo content (adds "Demo Content" to the meta line)</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label htmlFor="is_active">Active (visible on the public site — off by default)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={
                saveMutation.isPending ||
                (previewUrl.length > 0 && imageStatus !== "ok")
              }
            >
              {saveMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this testimonial?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes “{confirmDelete?.customer_name}” permanently. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FieldRow({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="mb-1 block">{label}</Label>
      {children}
      {hint && !error ? (
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      ) : null}
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}